import { request } from "./http";

export interface Entitlements {
  learn: string[];
  practice: string[];
  reference: string[];
}

export interface ContentPack {
  code: string;
  name: string;
  level: string;
  modules: string[];
  price_minor: number;
  description: string;
}

export interface CreditPack {
  code: string;
  name: string;
  credits: number;
  price_minor: number;
  description: string;
}

export interface Store {
  entitlements: Entitlements;
  content_packs: ContentPack[];
  credit_packs: CreditPack[];
  currency: string;
}

export interface CreditBalance {
  balance: number;
  granted_this_month: number;
  spent_this_month: number;
  tokens_per_credit: number;
  /** What the tenant is granted each month. 0 = nothing recurring. */
  monthly_credits: number;
}

export interface OrderItem {
  code: string;
  name: string;
  price_minor: number;
}

export interface PaymentInfo {
  account_name: string;
  account_number: string;
  bank_name: string;
  ifsc: string;
  contact_email: string;
  note: string;
}

/** Bank-transfer instructions returned when an order is placed. */
export interface OrderInstructions {
  reference: string;
  amount_minor: number;
  currency: string;
  items: OrderItem[];
  payment: PaymentInfo;
}

export function getStore(): Promise<Store> {
  return request("/learning/store");
}

/** Place a pending order and get the bank-transfer instructions (no charge
 *  online — a super-admin unlocks the packs once the transfer arrives). */
export function checkout(packCodes: string[]): Promise<OrderInstructions> {
  return request("/learning/store/checkout", {
    method: "POST",
    body: { pack_codes: packCodes },
  });
}

export function myCredits(): Promise<CreditBalance> {
  return request("/me/ai-credits");
}

// --- super-admin ------------------------------------------------------------
export function grantPack(tenantId: string, packCode: string, reason = ""): Promise<unknown> {
  return request(`/platform/tenants/${tenantId}/grant-pack`, {
    method: "POST",
    body: { pack_code: packCode, reason },
  });
}

export function tenantCredits(tenantId: string): Promise<CreditBalance> {
  return request(`/platform/tenants/${tenantId}/ai-credits`);
}

/** Assign the tenant's monthly allowance. 0 falls back to their plan.
 *  Takes effect at the next monthly grant, never retroactively. */
export function setMonthlyCredits(
  tenantId: string,
  credits: number,
): Promise<CreditBalance> {
  return request(`/platform/tenants/${tenantId}/ai-credits/monthly`, {
    method: "PUT",
    body: { credits },
  });
}

export function adjustTenantCredits(
  tenantId: string,
  credits: number,
  reason: string,
): Promise<CreditBalance> {
  return request(`/platform/tenants/${tenantId}/ai-credits/adjust`, {
    method: "POST",
    body: { credits, reason },
  });
}
